/**
 * Pure date maths for Call Booking: the calendars, the bookings agenda, and
 * turning weekly shifts + call types into the slots the server will offer.
 *
 * Everything here is timezone-sensitive in the same way, and gets it right the
 * same way: a booking is an *instant* (offset-bearing ISO from the API), a day
 * is a *local calendar date* (bucketed locally), and a shift is a *UTC
 * wall-clock window on a weekday* that is converted to local time on the way
 * in and back on the way out (see "Shifts and call types" below).
 *
 * No React, no API — so all of this is unit-testable in isolation.
 */

import { formatLocalDate } from "../../../utils/formatLocalDate";
import { STATUS, isActiveBooking, normaliseStatus } from "./bookingStatus";

/** Boundaries for the preview's Morning/Afternoon/Evening grouping (local hours). */
const AFTERNOON_START_HOUR = 12;
const EVENING_START_HOUR = 17;

export const MINUTES_PER_DAY = 24 * 60;

/* ------------------------------------------------------------------ *
 * Time-of-day helpers — "minutes since local midnight" as the unit.
 * ------------------------------------------------------------------ */

/**
 * Format minutes-since-midnight as a 12-hour label, e.g. 570 → "9:30 am".
 *
 * @param {number} minutes
 * @returns {string}
 */
export function formatTimeOfDay(minutes) {
  const total = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hour24 = Math.floor(total / 60);
  const minute = total % 60;
  const suffix = hour24 < 12 ? "am" : "pm";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
}

/** Minutes since local midnight for a Date. */
export function minutesOfDay(date) {
  return date.getHours() * 60 + date.getMinutes();
}

/**
 * Parse an API timestamp into a Date, or null.
 *
 * Deliberately not a bare `new Date(value)`: `new Date(null)` is the epoch and
 * `new Date(undefined)` is Invalid Date, so a missing `start_time` would
 * otherwise silently bucket a booking onto 1 Jan 1970 rather than being dropped.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
function toDate(value) {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/* ------------------------------------------------------------------ *
 * Month grid
 * ------------------------------------------------------------------ */

/**
 * Whether a day is greyed on a calendar that asks for it — Sundays.
 *
 * Only a display rule now: the bookings calendar opens every day
 * (`allowClosed`), and shifts may be set on any weekday, Sunday included.
 *
 * @param {Date|null|undefined} date
 * @returns {boolean} False for anything that isn't a usable date.
 */
export function isClosedDay(date) {
  return date instanceof Date && !Number.isNaN(date.getTime())
    ? date.getDay() === 0
    : false;
}

/**
 * A Monday-first month grid, padded to whole weeks with nulls.
 *
 * `isClosed` marks Sundays; see `isClosedDay`.
 *
 * @param {number} year - Full year, e.g. 2026.
 * @param {number} month - 0-indexed month, matching Date.
 * @param {Date} [today] - Injectable for tests.
 * @returns {Array<Array<{ date: Date, key: string, day: number, isClosed: boolean, isPast: boolean, isToday: boolean }|null>>}
 */
export function buildMonthGrid(year, month, today = new Date()) {
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const first = new Date(year, month, 1);
  // getDay() is Sunday-first; shift so Monday === 0.
  const leadingBlanks = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < leadingBlanks; i += 1) cells.push(null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, month, day);
    cells.push({
      date,
      key: formatLocalDate(date),
      day,
      isClosed: isClosedDay(date),
      isPast: date < startOfToday,
      isToday: date.getTime() === startOfToday.getTime(),
    });
  }

  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/**
 * A local month as instants rather than calendar dates.
 *
 * `fetch-booking-records` takes real timestamps — it compares them directly
 * against the booking's `start_time` — so because the bounds here are absolute
 * instants, local month boundaries survive the round trip and nothing needs
 * widening or aligning against UTC midnight.
 *
 * @param {number} year
 * @param {number} month - 0-indexed.
 * @returns {{ fromDate: string, toDate: string }} Both ISO 8601 instants.
 */
export function monthRange(year, month) {
  const from = new Date(year, month, 1, 0, 0, 0, 0);
  // Day 0 of the next month is the last day of this one; 23:59:59.999 keeps the
  // upper bound inclusive, which is how the API compares it (`<=`).
  const to = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return { fromDate: from.toISOString(), toDate: to.toISOString() };
}

/* ------------------------------------------------------------------ *
 * Bookings
 * ------------------------------------------------------------------ */

/**
 * Bucket booking rows by the local calendar date of their start.
 *
 * A booking carries its own fixed `start_time`/`end_time` (it no longer points
 * at a stored slot). Rows with an unreadable `start_time` are dropped rather
 * than placed on an invented day. Each bucket sorts **ascending**, which is what
 * a day's agenda wants — the API itself returns `start_time DESC`, which is what
 * the history list wants, so keeping both orders means neither list re-sorts.
 *
 * @param {Array<Object>} records - Rows from `GET /fetch-booking-records`.
 * @returns {Record<string, Array<Object>>} Keyed `YYYY-MM-DD`, rows carrying
 *   `startsAt` and (when readable) `endsAt`.
 */
export function bucketBookingsByLocalDate(records) {
  const buckets = {};
  (Array.isArray(records) ? records : []).forEach((record) => {
    const startsAt = toDate(record?.start_time);
    if (!startsAt) return;
    const key = formatLocalDate(startsAt);
    (buckets[key] || (buckets[key] = [])).push({
      ...record,
      startsAt,
      endsAt: toDate(record?.end_time),
    });
  });
  Object.values(buckets).forEach((list) =>
    list.sort((a, b) => a.startsAt - b.startsAt)
  );
  return buckets;
}

/**
 * Split a day's slots into the preview's three periods, in display order.
 *
 * @param {Array<Object>} slots - Rows carrying a `startsAt` Date.
 * @returns {Array<{ id: string, label: string, caption: string, slots: Array<Object> }>}
 */
export function groupByPeriod(slots) {
  const groups = {
    morning: [],
    afternoon: [],
    evening: [],
  };

  (Array.isArray(slots) ? slots : []).forEach((slot) => {
    const hour = slot.startsAt.getHours();
    if (hour < AFTERNOON_START_HOUR) groups.morning.push(slot);
    else if (hour < EVENING_START_HOUR) groups.afternoon.push(slot);
    else groups.evening.push(slot);
  });

  return [
    { id: "morning", label: "Morning", caption: "Before 12:00 PM", slots: groups.morning },
    { id: "afternoon", label: "Afternoon", caption: "12:00 – 5:00 PM", slots: groups.afternoon },
    { id: "evening", label: "Evening", caption: "After 5:00 PM", slots: groups.evening },
  ];
}

/**
 * "9:30 – 10:00 am" for a booking or slot carrying `startsAt` and `endsAt`
 * (or the raw `end_time`).
 *
 * The am/pm marker is printed once when both ends share it, twice when they
 * straddle noon — "11:30 am – 12:00 pm" has to say both to be unambiguous.
 *
 * @param {{ startsAt: Date, endsAt?: Date, end_time?: string }} slot
 * @returns {string}
 */
export function formatSlotRange(slot) {
  const start = toDate(slot?.startsAt);
  const end = toDate(slot?.endsAt) || toDate(slot?.end_time);
  if (!start || !end) return "";

  const startLabel = formatTimeOfDay(minutesOfDay(start));
  const endLabel = formatTimeOfDay(minutesOfDay(end));
  const sameHalf = (start.getHours() < 12) === (end.getHours() < 12);
  return sameHalf
    ? `${startLabel.replace(/ (am|pm)$/, "")} – ${endLabel}`
    : `${startLabel} – ${endLabel}`;
}

/* ------------------------------------------------------------------ *
 * Shifts and call types
 *
 * A shift is stored as a UTC wall-clock window on an ISO weekday (Mon=1 …
 * Sun=7) and may not cross UTC midnight; it repeats every week while it is in
 * effect — from `effective_from` until `effective_to` (`be3e423`), so a change
 * of hours is a new shift from a date, not an edit of the past. The UI works
 * in local time, so everything below converts through one unit — **minutes
 * since local Monday 00:00** ("week minutes", 0 … 10080) — which makes a
 * weekday change across midnight plain arithmetic rather than a special case.
 * ------------------------------------------------------------------ */

export const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

const ORDINAL_WORDS = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"];

/** 1 → "1st", 2 → "2nd", 11 → "11th", 23 → "23rd". */
function ordinal(n) {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
}

/**
 * A department's configured tiers, in call order, with how each is named.
 *
 * Tiers are department-wide `ShiftCallType` rows, as many as the department
 * sets (Diet Utils / Workout Utils). The backend picks the highest `call_no` ≤
 * the member's call number, so the highest tier repeats for every later call —
 * that one is the **Regular call** — and each other tier covers the call
 * numbers up to the next configured one. `call_no` is 0-based; `appliesTo`
 * says it 1-based, the way people count calls.
 *
 * `short` is the word the "fits 3 first · 7 regular" lines use.
 *
 * @param {Record<number, Object>} tiers - From `callTypesByTier`.
 * @returns {Array<{ callNo: number, key: string, label: string, short: string, appliesTo: string }>}
 */
export function callTierList(tiers) {
  const callNos = Object.keys(tiers || {})
    .map(Number)
    .filter(Number.isInteger)
    .sort((a, b) => a - b);
  return callNos.map((callNo, index) => {
    const next = callNos[index + 1];
    const isRegular = next === undefined && callNo > 0;
    const word = ORDINAL_WORDS[callNo] || `Call ${callNo + 1}`;
    const label = isRegular ? "Regular call" : ORDINAL_WORDS[callNo] ? `${word} call` : word;
    const from = ordinal(callNo + 1);
    let appliesTo;
    if (next === undefined) appliesTo = `${from} call onward`;
    else if (next === callNo + 1) appliesTo = `${from} call`;
    else appliesTo = `${from} – ${ordinal(next)} call`;
    return {
      callNo,
      key: isRegular ? "regular" : callNo === 0 ? "first" : `call-${callNo}`,
      label,
      short: isRegular ? "regular" : label.replace(/ call$/, "").toLowerCase(),
      appliesTo,
    };
  });
}

/**
 * The latest end a stored `time` can say. Python's `time` has no 24:00, so a
 * UTC piece that runs to midnight is sent as 23:59:59 — see `toTimeString`.
 */
const DAY_END_TIME = "23:59:59";

/** The user's current UTC offset in minutes (IST → 330). */
export function localOffsetMinutes(date = new Date()) {
  return -date.getTimezoneOffset();
}

/**
 * Parse a stored `time` ("09:30:00", "09:30", "09:30:00.000Z") into minutes
 * since midnight, keeping seconds as a fraction so a generated grid matches the
 * server's to the second. `DAY_END_TIME` therefore reads as 1439.98, not 1440 —
 * the server will not offer a call ending at midnight in such a piece, so
 * neither may the preview. `utcShiftsToLocal` joins pieces up to a minute
 * apart, which is what absorbs the missing second for display.
 *
 * @param {string} value
 * @returns {number|null}
 */
export function parseTimeString(value) {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(value ?? "").trim());
  if (!match) return null;
  const [hours, minutes, seconds = "0"] = match.slice(1);
  return Number(hours) * 60 + Number(minutes) + Number(seconds) / 60;
}

/**
 * Minutes since midnight → the `"HH:MM:SS"` a shift or call-type write wants.
 * 1440 (a piece ending at UTC midnight) becomes `DAY_END_TIME`; anything else
 * is rounded to the minute.
 *
 * @param {number} minutes - 0 … 1440.
 * @returns {string}
 */
export function toTimeString(minutes) {
  const total = Math.round(minutes);
  if (total >= MINUTES_PER_DAY) return DAY_END_TIME;
  const safe = Math.max(0, total);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(safe / 60))}:${pad(safe % 60)}:00`;
}

/** Keep a week minute inside [0, 10080). */
function wrapWeek(minutes) {
  return ((minutes % MINUTES_PER_WEEK) + MINUTES_PER_WEEK) % MINUTES_PER_WEEK;
}

/** ISO day number (1 … 7) for a zero-based day index, wrapping the week. */
function dayNumberOf(dayIndex) {
  return (((dayIndex % 7) + 7) % 7) + 1;
}

/**
 * A local shift → the one or two UTC pieces the backend can store.
 *
 * IST Mon 04:00–06:00 is UTC Sun 22:30 → Mon 00:30, which crosses UTC midnight
 * and so is two rows: Sun 22:30–24:00 and Mon 00:00–00:30.
 *
 * @param {{ dayNumber: number, startMinutes: number, endMinutes: number }} shift - Local.
 * @param {number} [offsetMinutes] - Local UTC offset; defaults to the browser's.
 * @returns {Array<{ day_number: number, start_time: string, end_time: string }>}
 */
export function localShiftToUtc(
  { dayNumber, startMinutes, endMinutes },
  offsetMinutes = localOffsetMinutes()
) {
  if (!Number.isInteger(dayNumber) || dayNumber < 1 || dayNumber > 7) return [];
  if (!(endMinutes > startMinutes)) return [];

  const absStart = (dayNumber - 1) * MINUTES_PER_DAY + startMinutes - offsetMinutes;
  const absEnd = (dayNumber - 1) * MINUTES_PER_DAY + endMinutes - offsetMinutes;

  const pieces = [];
  const firstDay = Math.floor(absStart / MINUTES_PER_DAY);
  const lastDay = Math.floor((absEnd - 1) / MINUTES_PER_DAY);
  for (let day = firstDay; day <= lastDay; day += 1) {
    const dayStart = day * MINUTES_PER_DAY;
    const from = Math.max(absStart, dayStart) - dayStart;
    const to = Math.min(absEnd, dayStart + MINUTES_PER_DAY) - dayStart;
    if (to <= from) continue;
    pieces.push({
      day_number: dayNumberOf(day),
      start_time: toTimeString(from),
      end_time: toTimeString(to),
    });
  }
  return pieces;
}

/** An API instant → Date, or null when absent or unreadable. */
export function parseInstant(value) {
  if (value == null || value === "") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Local midnight at the start of `date`'s day. */
export function localDayStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Local midnight of the first ISO weekday `dayNumber` on or after `date`'s day. */
export function firstWeekdayFrom(date, dayNumber) {
  const day = localDayStart(date);
  const ahead = (dayNumber - (((day.getDay() + 6) % 7) + 1) + 7) % 7;
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + ahead);
}

/**
 * When a weekly shift added `now` first runs: the local start time on the
 * first date of `dayNumber` that hasn't ended yet — today's start when still
 * ahead, `now` while today's run is under way (the server keeps a slot whose
 * start is in effect, so the rest of today still gets slots), otherwise a week
 * on. What an ADD's `effective_from` is.
 *
 * @param {Date} now
 * @param {number} dayNumber - ISO weekday, 1 = Monday.
 * @param {number} startMinutes - Local minutes from midnight.
 * @param {number} endMinutes - Local minutes from midnight.
 * @returns {Date}
 */
export function nextShiftStart(now, dayNumber, startMinutes, endMinutes) {
  const day = firstWeekdayFrom(now, dayNumber);
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, startMinutes);
  if (start >= now) return start;
  const end = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, endMinutes);
  return end > now ? now : new Date(day.getFullYear(), day.getMonth(), day.getDate() + 7, 0, startMinutes);
}

/**
 * Stored UTC shifts → local shifts, one per contiguous local window per local
 * day, each keeping the stored `pieces` it was built from so an edit or a
 * delete can act on all of them.
 *
 * Pieces that touch (one ends at UTC midnight, the next starts there on the
 * following day) are joined back into the one shift the manager entered — but
 * only pieces with the same effective period, since those were written
 * together; an old shift's piece never joins its successor's. A joined window
 * that crosses *local* midnight is cut there again, since the week table has
 * one row per local day.
 *
 * Since `be3e423` every row carries `effective_from` (inclusive) and
 * `effective_to` (exclusive, null = carries forward). Each local shift keeps
 * them as `effectiveFrom` / `effectiveTo` Dates; `shiftsOnDate` picks the ones
 * a given date shows. A row without `effective_from` reads as always in effect.
 * A row removed before it started (`isVoidedPeriod`) is left out entirely: it
 * stays on record but applies to no date, and its day must not show it.
 *
 * @param {Array<Object>} coachShifts - Rows from `GET /fetch-coach-shift`.
 * @param {number} [offsetMinutes]
 * @returns {Array<{ key: string, dayNumber: number, startMinutes: number, endMinutes: number, effectiveFrom: Date|null, effectiveTo: Date|null, pieces: Array<Object> }>}
 *   Sorted by day, then start.
 */
export function utcShiftsToLocal(coachShifts, offsetMinutes = localOffsetMinutes()) {
  const spans = [];
  (Array.isArray(coachShifts) ? coachShifts : []).forEach((row) => {
    const dayNumber = Number(row?.day_number);
    const start = parseTimeString(row?.start_time);
    const end = parseTimeString(row?.end_time);
    if (!Number.isInteger(dayNumber) || dayNumber < 1 || dayNumber > 7) return;
    if (start === null || end === null || end <= start) return;
    const effectiveFrom = parseInstant(row?.effective_from);
    const effectiveTo = parseInstant(row?.effective_to);
    if (isVoidedPeriod(effectiveFrom, effectiveTo)) return;
    const absStart = wrapWeek((dayNumber - 1) * MINUTES_PER_DAY + start + offsetMinutes);
    spans.push({
      start: absStart,
      end: absStart + (end - start),
      effectiveFrom,
      effectiveTo,
      period: `${effectiveFrom?.getTime() ?? ""}|${effectiveTo?.getTime() ?? ""}`,
      pieces: [row],
    });
  });
  spans.sort((a, b) => a.start - b.start || a.period.localeCompare(b.period));

  // Join touching spans of one period. Less than a minute apart counts as
  // touching, which is what absorbs the second a stored 23:59:59 falls short
  // of midnight.
  const merged = [];
  spans.forEach((span) => {
    const last = [...merged]
      .reverse()
      .find((candidate) => candidate.period === span.period && Math.abs(span.start - candidate.end) < 1);
    if (last) {
      last.end = Math.max(last.end, span.end);
      last.pieces.push(...span.pieces);
    } else {
      merged.push({ ...span, pieces: [...span.pieces] });
    }
  });
  // The week is a circle: a span ending at Sunday midnight joins the same
  // period's span starting at Monday midnight.
  merged
    .filter((span) => Math.abs(span.end - MINUTES_PER_WEEK) < 1)
    .forEach((last) => {
      const firstIndex = merged.findIndex(
        (span) => span !== last && span.period === last.period && span.start < 1
      );
      if (firstIndex < 0) return;
      const [first] = merged.splice(firstIndex, 1);
      last.end = first.end + MINUTES_PER_WEEK;
      last.pieces.push(...first.pieces);
    });

  const shifts = [];
  merged.forEach((span) => {
    const firstDay = Math.floor(span.start / MINUTES_PER_DAY);
    const lastDay = Math.floor((span.end - 1e-6) / MINUTES_PER_DAY);
    for (let day = firstDay; day <= lastDay; day += 1) {
      const dayStart = day * MINUTES_PER_DAY;
      const startMinutes = Math.round(Math.max(span.start, dayStart) - dayStart);
      const endMinutes = Math.round(Math.min(span.end, dayStart + MINUTES_PER_DAY) - dayStart);
      if (endMinutes <= startMinutes) continue;
      const dayNumber = dayNumberOf(day);
      shifts.push({
        key: span.pieces.map((piece) => piece.id).join("+") + `@${dayNumber}`,
        dayNumber,
        startMinutes,
        endMinutes,
        effectiveFrom: span.effectiveFrom,
        effectiveTo: span.effectiveTo,
        pieces: span.pieces,
      });
    }
  });
  return shifts.sort(
    (a, b) => a.dayNumber - b.dayNumber || a.startMinutes - b.startMinutes
  );
}

/**
 * Whether a local shift shows on `date`'s local day: it has started by the
 * day's end and is not removed before the day is over. A shift removed
 * part-way through a day (writes close it at the moment of removal) is gone
 * from that whole day, even if its hours already ran.
 *
 * @param {{ effectiveFrom?: Date|null, effectiveTo?: Date|null }} shift
 * @param {Date} date
 * @returns {boolean}
 */
export function shiftAppliesOn(shift, date) {
  const dayStart = localDayStart(date);
  const dayEnd = new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate() + 1);
  return (
    (!shift?.effectiveFrom || shift.effectiveFrom < dayEnd) &&
    (!shift?.effectiveTo || shift.effectiveTo >= dayEnd)
  );
}

/**
 * The local shifts one calendar date shows: that weekday's, in effect that day.
 *
 * @param {Array<Object>} localShifts - From `utcShiftsToLocal`.
 * @param {Date} date
 * @returns {Array<Object>}
 */
export function shiftsOnDate(localShifts, date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return [];
  const dayNumber = ((date.getDay() + 6) % 7) + 1;
  return (Array.isArray(localShifts) ? localShifts : []).filter(
    (shift) => shift.dayNumber === dayNumber && shiftAppliesOn(shift, date)
  );
}

/**
 * The shifts a new one added `now` for `startMinutes`–`endMinutes` could
 * collide with: those still in effect when it first runs on their weekday
 * (`nextShiftStart`) — the server's test, an overlap of effective periods. A
 * shift that ends by then can't, e.g. a Wednesday shift closed at Wed 00:00
 * doesn't block a Wednesday shift added on Tuesday.
 *
 * @param {Array<Object>} localShifts
 * @param {Date} now
 * @param {number} startMinutes - The new shift's local start.
 * @param {number} endMinutes - The new shift's local end.
 * @returns {Array<Object>}
 */
export function shiftsFrom(localShifts, now, startMinutes, endMinutes) {
  return (Array.isArray(localShifts) ? localShifts : []).filter(
    (shift) =>
      !shift.effectiveTo || shift.effectiveTo > nextShiftStart(now, shift.dayNumber, startMinutes, endMinutes)
  );
}

/**
 * Whether a local window collides with another shift on the same day. Half
 * open, like the backend: touching edges are fine. `ignoreKey` skips the shift
 * being edited.
 *
 * @param {{ dayNumber: number, startMinutes: number, endMinutes: number }} candidate
 * @param {Array<Object>} localShifts - From `utcShiftsToLocal`.
 * @param {string} [ignoreKey]
 * @returns {Object|null} The shift it overlaps, if any.
 */
export function findShiftOverlap(candidate, localShifts, ignoreKey) {
  return (
    (Array.isArray(localShifts) ? localShifts : []).find(
      (shift) =>
        shift.key !== ignoreKey &&
        shift.dayNumber === candidate.dayNumber &&
        candidate.startMinutes < shift.endMinutes &&
        shift.startMinutes < candidate.endMinutes
    ) || null
  );
}

/** A Date → the offset-bearing instant `effective_from`/`effective_to` want (a naive one is a 422). */
const toInstant = (date) => (date ? date.toISOString() : null);

/**
 * How long a shift removed before it started is left in effect: one second,
 * which no slot can start in. The server can't DELETE a shift that hasn't
 * started (see `planShiftRemoval`), so this sliver is how one is removed.
 */
export const VOID_SPAN_MS = 1000;

/** Whether an effective period is a removed-before-it-started shift's — on record, in effect for no slot. */
export const isVoidedPeriod = (from, to) => Boolean(from && to && to - from <= VOID_SPAN_MS);

/**
 * `[from, to)` periods (to = null: open) covering `from` onward, minus the
 * given void slivers — the effective periods an ADD can take without
 * overlapping any of them.
 */
function periodsAroundVoids(from, voids) {
  const periods = [];
  let cursor = from;
  [...voids]
    .sort((a, b) => a.from - b.from)
    .forEach((sliver) => {
      if (sliver.to <= cursor) return;
      if (sliver.from > cursor) periods.push({ from: cursor, to: sliver.from });
      cursor = sliver.to;
    });
  periods.push({ from: cursor, to: null });
  return periods;
}

/**
 * ADD items for one local window on each of the given days, each in effect
 * from that day's first run that hasn't ended (`nextShiftStart`: the date +
 * the local start time, or `now` while today's run is under way, sent as UTC;
 * `effective_from` is required on ADD), with
 * no end. Every UTC piece of a day shares that instant.
 *
 * Since `bd2e283` an ADD overlapping a stored shift's hours and dates is a
 * 400 — it no longer supersedes an open one or moves past a closed one. The
 * form checks visible shifts itself (`validateShiftDraft`), but a shift
 * removed before it started stays on record as a one-second sliver
 * (`isVoidedPeriod`) that `utcShiftsToLocal` hides and the server still counts.
 * So each UTC piece is cut around any sliver on the same UTC weekday and hours
 * that it would cover: a sliver still ahead (left by a shift that was added
 * for a future date, then removed) is written up to and again after it.
 *
 * @param {{ dayNumbers: Array<number>, startMinutes: number, endMinutes: number }} draft
 * @param {{ employeeId: string, weekdayIdByNumber: Map<number, string>, coachShiftTypeId: string, offsetMinutes?: number, storedShifts?: Array<Object> }} ctx
 *   `storedShifts` — the coach's raw `fetch-coach-shift` rows, voided ones included.
 * @param {Date} now - When the shift is added.
 * @returns {Array<Object>} `CoachShiftItem`s, `operation: "ADD"`.
 */
export function buildShiftItems(
  { dayNumbers, startMinutes, endMinutes },
  { employeeId, weekdayIdByNumber, coachShiftTypeId, offsetMinutes = localOffsetMinutes(), storedShifts = [] },
  now
) {
  const slivers = (Array.isArray(storedShifts) ? storedShifts : [])
    .map((row) => ({
      dayNumber: Number(row?.day_number),
      start: parseTimeString(row?.start_time),
      end: parseTimeString(row?.end_time),
      from: parseInstant(row?.effective_from),
      to: parseInstant(row?.effective_to),
    }))
    .filter((row) => isVoidedPeriod(row.from, row.to));

  return (Array.isArray(dayNumbers) ? dayNumbers : []).flatMap((dayNumber) => {
    const firstRun = nextShiftStart(now, dayNumber, startMinutes, endMinutes);
    return localShiftToUtc({ dayNumber, startMinutes, endMinutes }, offsetMinutes).flatMap((piece) => {
      const start = parseTimeString(piece.start_time);
      const end = parseTimeString(piece.end_time);
      const voids = slivers.filter(
        (row) => row.dayNumber === piece.day_number && row.start < end && start < row.end
      );
      return periodsAroundVoids(firstRun, voids).map((period) => ({
        operation: "ADD",
        employee_id: employeeId,
        weekday_id: weekdayIdByNumber.get(piece.day_number),
        coach_shift_type_id: coachShiftTypeId,
        start_time: piece.start_time,
        end_time: piece.end_time,
        effective_from: toInstant(period.from),
        ...(period.to ? { effective_to: toInstant(period.to) } : {}),
      }));
    });
  });
}

/**
 * The one request that removes a local shift **from `now` onward** — always
 * a close (UPDATE `effective_to` on every stored piece, `effective_from`
 * left as it was), never a DELETE.
 *
 * A DELETE ends the shift *now* and soft-deletes the row out of history, and
 * for a shift that hasn't started it is a 400 (`effective_to` would precede
 * `effective_from`). Since `bd2e283` both an UPDATE and a DELETE are refused
 * with a 409 listing the calls while the shift holds **any** upcoming booking,
 * whatever the date — those calls have to be cancelled or rescheduled first.
 *
 * - **The shift has started** (`effectiveFrom` at or before `now`, or
 *   unknown): closed at `now`, so everything before keeps it.
 * - **It hasn't started yet** (an ADD starts at its first run, which may be
 *   days ahead): it can't close at or before its own start
 *   (a 400), so it is closed `VOID_SPAN_MS` after it — on record, in effect for
 *   no slot — and `utcShiftsToLocal` leaves such a row out (`buildShiftItems`
 *   still steps around it).
 *
 * @param {{ pieces: Array<Object>, effectiveFrom?: Date|null, effectiveTo?: Date|null }} shift - From `utcShiftsToLocal`.
 * @param {Date} now
 * @returns {Array<Array<Object>>} Batches, in send order — one, or none when the shift already ends by then.
 */
export function planShiftRemoval(shift, now) {
  const pieces = Array.isArray(shift?.pieces) ? shift.pieces : [];
  const started = !shift?.effectiveFrom || shift.effectiveFrom < now;
  const closeAt = started ? now : new Date(shift.effectiveFrom.getTime() + VOID_SPAN_MS);
  if (pieces.length === 0 || (shift?.effectiveTo && shift.effectiveTo <= closeAt)) return [];
  return [pieces.map((piece) => ({ operation: "UPDATE", id: piece.id, effective_to: toInstant(closeAt) }))];
}

/**
 * The booked calls a refused shift change names, read out of the server's
 * 409 (`validate_bookings_still_fit`):
 * "This change would leave booked calls outside their shift - cancel or move
 * them first: booking {id} at {start} on shift {shift} ({reason}); …".
 * Since `bd2e283` the reason for a removal is "the shift's end date cannot
 * change while it has upcoming bookings" — any upcoming call blocks it.
 *
 * The API returns only that sentence, so this is how the dialog can list the
 * calls in local time instead of showing raw ids and UTC instants. Anything
 * that doesn't parse is left out; an empty result means "show the message".
 *
 * @param {string} message
 * @returns {Array<{ id: string, startsAt: Date, shiftId: string, reason: string|null }>} Sorted by start.
 */
export function parseStrandedBookings(message) {
  const pattern = /booking ([0-9a-f-]{36}) at (\S+) on shift ([0-9a-f-]{36})(?: \(([^)]*)\))?/gi;
  const found = [];
  let match;
  while ((match = pattern.exec(String(message || "")))) {
    const startsAt = parseInstant(match[2]);
    if (startsAt) found.push({ id: match[1], startsAt, shiftId: match[3], reason: match[4] || null });
  }
  return found.sort((a, b) => a.startsAt - b.startsAt);
}

/**
 * A call type's length in minutes, from `duration_time` / `buffer_time`.
 *
 * `GET /fetch-shift-call-type` returns lengths **only** as `"HH:MM:SS"` since
 * `7428c14` — it dropped the `duration_minutes`/`buffer_minutes` it used to
 * repeat them as. `POST /modify-shift-call-type` still returns the minutes, so
 * those are read first when present and the time string otherwise.
 *
 * @param {Object} row - A call-type row.
 * @param {"duration"|"buffer"} field
 * @returns {number|null} Null when neither form can be read.
 */
export function callTypeLengthMinutes(row, field) {
  const minutes = Number(row?.[`${field}_minutes`]);
  if (row?.[`${field}_minutes`] != null && Number.isFinite(minutes)) return minutes;
  return parseTimeString(row?.[`${field}_time`]);
}

/**
 * A department's configured tiers, keyed by `call_no`. Only department-wide
 * rows (`plan_id` null) count — plan overrides are not something this app sets.
 *
 * @param {Array<Object>} shiftCallTypes - Rows from `GET /fetch-shift-call-type`.
 * @param {string} departmentId
 * @returns {Record<number, { id: string, durationMinutes: number, bufferMinutes: number, assignedByName: string|null }>}
 */
export function callTypesByTier(shiftCallTypes, departmentId) {
  const tiers = {};
  (Array.isArray(shiftCallTypes) ? shiftCallTypes : []).forEach((row) => {
    if (row?.plan_id) return;
    if (departmentId && String(row?.department_id) !== String(departmentId)) return;
    const callNo = Number(row?.call_no);
    const durationMinutes = callTypeLengthMinutes(row, "duration");
    if (!Number.isInteger(callNo) || !(durationMinutes > 0)) return;
    tiers[callNo] = {
      id: row.id,
      durationMinutes,
      bufferMinutes: callTypeLengthMinutes(row, "buffer") || 0,
      assignedByName: row?.assigned_by_name || null,
    };
  });
  return tiers;
}

/**
 * A department's tiers, found by its **title** — which is what the frontend
 * knows a coach's department by (`getCoachDepartmentTitles`, a roster row's
 * `departmentTitle`) — along with the department's id, read off the rows.
 *
 * Also returns every call type's buffer by id, which the slot preview needs to
 * widen an existing booking by *its own* call type's buffer, the way the
 * server's conflict check does.
 *
 * @param {Array<Object>} shiftCallTypes - Rows from `GET /fetch-shift-call-type`.
 * @param {string|null|undefined} departmentTitle
 * @returns {{ departmentId: string|null, tiers: Record<number, Object>, bufferByCallTypeId: Map<string, number> }}
 */
export function callTypesForDepartment(shiftCallTypes, departmentTitle) {
  const rows = Array.isArray(shiftCallTypes) ? shiftCallTypes : [];
  const bufferByCallTypeId = new Map(
    rows
      .filter((row) => row?.id)
      .map((row) => [String(row.id), callTypeLengthMinutes(row, "buffer") || 0])
  );
  const wanted = String(departmentTitle || "").trim().toLowerCase();
  const match = wanted
    ? rows.find((row) => String(row?.department || "").trim().toLowerCase() === wanted)
    : null;
  const departmentId = match?.department_id ?? null;
  return {
    departmentId,
    tiers: departmentId ? callTypesByTier(rows, departmentId) : {},
    bufferByCallTypeId,
  };
}

/**
 * Every call type's label by id — "First call", "Second call", "Regular call" —
 * named the way the Shifts tab names its department's tiers (`callTierList`).
 *
 * A booking points at its call type only through `shift_call_type_id`, so this
 * is what turns that id into words. Each department is labelled against its own
 * tiers. A plan-specific row (which `callTypesByTier` leaves out) takes the
 * label of the department tier with the same `call_no`, and one with no such
 * tier its plain ordinal. Only live rows come back from the fetch, so a booking
 * whose call type was since deleted has no entry — callers show nothing then.
 *
 * Labels follow the tiers as they are configured **now**: the highest tier is
 * the "Regular call", so adding a tier above it renames past bookings too.
 *
 * @param {Array<Object>} shiftCallTypes - Rows from `GET /fetch-shift-call-type`.
 * @returns {Map<string, string>}
 */
export function callTypeLabelsById(shiftCallTypes) {
  const rows = Array.isArray(shiftCallTypes) ? shiftCallTypes : [];
  const tiersByDepartment = new Map();
  const labelsFor = (departmentId) => {
    // No department means no tiers to rank against — `callTypesByTier` would
    // read a missing id as "every department" and mix their tiers together.
    if (departmentId == null || departmentId === "") return new Map();
    const key = String(departmentId);
    if (!tiersByDepartment.has(key)) {
      const list = callTierList(callTypesByTier(rows, departmentId));
      tiersByDepartment.set(key, new Map(list.map((tier) => [tier.callNo, tier.label])));
    }
    return tiersByDepartment.get(key);
  };

  const labels = new Map();
  rows.forEach((row) => {
    const callNo = Number(row?.call_no);
    if (!row?.id || !Number.isInteger(callNo) || callNo < 0) return;
    const label =
      labelsFor(row.department_id).get(callNo) ||
      (ORDINAL_WORDS[callNo] ? `${ORDINAL_WORDS[callNo]} call` : `Call ${callNo + 1}`);
    labels.set(String(row.id), label);
  });
  return labels;
}

/**
 * The call-type label to show on a booking, or null when there is none.
 *
 * A `call_type` the server sends on the record wins — a string, or an object
 * carrying `label`/`title` — so the frontend stops guessing the moment the
 * backend says. Until then it is looked up from `shift_call_type_id`.
 *
 * @param {Object} booking
 * @param {Map<string, string>} [labelsById] - From `callTypeLabelsById`.
 * @returns {string|null}
 */
export function callTypeLabelOf(booking, labelsById) {
  const sent = booking?.call_type;
  const fromServer =
    typeof sent === "string" ? sent : typeof sent === "object" && sent ? sent.label || sent.title : "";
  if (typeof fromServer === "string" && fromServer.trim()) return fromServer.trim();
  if (booking?.shift_call_type_id == null || !labelsById) return null;
  return labelsById.get(String(booking.shift_call_type_id)) || null;
}

/**
 * The call-type writes for a department's edited tiers: UPDATE where the tier
 * already has a row (the unique index refuses a second), ADD where it does not,
 * nothing where nothing changed. Only the tiers in `draft` are touched.
 *
 * @param {string} departmentId
 * @param {Record<number, { id: string, durationMinutes: number, bufferMinutes: number }>} existing - From `callTypesByTier`.
 * @param {Record<number, { durationMinutes: number, bufferMinutes: number }>} draft - Keyed by `call_no`.
 * @returns {Array<Object>} `ShiftCallTypeItem`s.
 */
export function buildCallTypeItems(departmentId, existing, draft) {
  const callNos = Object.keys(draft || {})
    .map(Number)
    .filter((callNo) => Number.isInteger(callNo) && callNo >= 0)
    .sort((a, b) => a - b);
  return callNos.flatMap((callNo) => {
    const wanted = draft[callNo];
    if (!wanted || !(wanted.durationMinutes > 0)) return [];
    const bufferMinutes = Math.max(0, Number(wanted.bufferMinutes) || 0);
    const stored = existing?.[callNo];
    if (
      stored &&
      stored.durationMinutes === wanted.durationMinutes &&
      stored.bufferMinutes === bufferMinutes
    ) {
      return [];
    }
    const times = {
      duration_time: toTimeString(wanted.durationMinutes),
      buffer_time: toTimeString(bufferMinutes),
    };
    return stored
      ? [{ operation: "UPDATE", id: stored.id, ...times }]
      : [
          {
            operation: "ADD",
            department_id: departmentId,
            plan_id: null,
            call_no: callNo,
            ...times,
          },
        ];
  });
}

/**
 * The slots one call type yields in a shift, as the server generates them
 * (`slots_for_shift_on`): walk each stored UTC piece in steps of
 * `duration + buffer`, keeping a slot only while the whole call fits before the
 * piece ends — the buffer after the last may run past it. Generated **per
 * piece**, exactly as the backend does, so a shift split at UTC midnight
 * restarts its grid at 00:00 UTC the way real slots will.
 *
 * @param {{ dayNumber: number, startMinutes: number, endMinutes: number, pieces?: Array<Object> }} shift
 *   A local shift; `pieces` default to `localShiftToUtc` for an unsaved draft.
 * @param {{ durationMinutes: number, bufferMinutes?: number }} callType
 * @param {number} [offsetMinutes]
 * @returns {Array<{ startMinutes: number, endMinutes: number }>} Local minutes
 *   relative to the shift's own day, chronological.
 */
export function shiftSlots(shift, callType, offsetMinutes = localOffsetMinutes()) {
  const duration = Number(callType?.durationMinutes);
  const buffer = Math.max(0, Number(callType?.bufferMinutes) || 0);
  if (!(duration > 0) || !shift) return [];

  const pieces =
    Array.isArray(shift.pieces) && shift.pieces.length
      ? shift.pieces
      : localShiftToUtc(shift, offsetMinutes);
  const shiftDayStart = (shift.dayNumber - 1) * MINUTES_PER_DAY;

  const slots = [];
  pieces.forEach((piece) => {
    const start = parseTimeString(piece.start_time);
    const end = parseTimeString(piece.end_time);
    const dayNumber = Number(piece.day_number);
    if (start === null || end === null || end <= start) return;
    for (let cursor = start; cursor + duration <= end; cursor += duration + buffer) {
      // Back to local week minutes, then relative to the shift's local day. The
      // pieces of one shift sit within a day of it, so the nearest wrap is right.
      let local =
        wrapWeek((dayNumber - 1) * MINUTES_PER_DAY + cursor + offsetMinutes) - shiftDayStart;
      if (local > MINUTES_PER_WEEK / 2) local -= MINUTES_PER_WEEK;
      if (local < -MINUTES_PER_WEEK / 2) local += MINUTES_PER_WEEK;
      slots.push({ startMinutes: local, endMinutes: local + duration });
    }
  });
  return slots.sort((a, b) => a.startMinutes - b.startMinutes);
}

/**
 * The summary line under a shift form: length, and how many slots each tier
 * fits. A tier with no configured call type is left out rather than shown as 0.
 *
 * @param {Object} shift - As `shiftSlots` takes it.
 * @param {Record<number, Object>} tiers - From `callTypesByTier`.
 * @param {number} [offsetMinutes]
 * @returns {{ minutes: number, counts: Array<{ key: string, label: string, count: number }> }}
 */
export function summariseShift(shift, tiers, offsetMinutes = localOffsetMinutes()) {
  const minutes = Math.max(0, (shift?.endMinutes ?? 0) - (shift?.startMinutes ?? 0));
  const counts = callTierList(tiers).map(({ callNo, key, label, short }) => ({
    key,
    label,
    short,
    count: shiftSlots(shift, tiers[callNo], offsetMinutes).length,
  }));
  return { minutes, counts };
}

const slotInstant = (date, minutes) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, Math.round(minutes * 60));

/**
 * A shift's slots on one local date that the server would generate: only those
 * starting inside the shift's effective period — which only bites on the day
 * a shift starts or ends. Without a date, all of them.
 */
function slotsInEffect(shift, callType, offsetMinutes, date) {
  const slots = shiftSlots(shift, callType, offsetMinutes);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return slots;
  return slots.filter(({ startMinutes }) => {
    const startsAt = slotInstant(date, startMinutes);
    return (
      (!shift.effectiveFrom || startsAt >= shift.effectiveFrom) &&
      (!shift.effectiveTo || startsAt < shift.effectiveTo)
    );
  });
}

/**
 * One tier's slots across a local day's shifts, as instants on a given date,
 * each marked with the live booking it collides with.
 *
 * Collision is the server's `find_conflict`: each side widened by its own
 * trailing buffer, so a long first call still blocks the short slots it
 * covers and the gap after it. A booking's buffer comes from its own call
 * type (`bufferByCallTypeId`), defaulting to none. Cancelled bookings free
 * their time and are ignored.
 *
 * @param {Date} date - The local calendar day.
 * @param {Array<Object>} dayShifts - That day's local shifts.
 * @param {{ durationMinutes: number, bufferMinutes?: number }} callType
 * @param {Array<Object>} bookings - Carrying `startsAt`/`endsAt` Dates.
 * @param {{ bufferByCallTypeId?: Map<string, number>, offsetMinutes?: number }} [options]
 * @returns {Array<{ startsAt: Date, endsAt: Date, booking: Object|null }>}
 */
export function shiftSlotsOnDate(
  date,
  dayShifts,
  callType,
  bookings,
  { bufferByCallTypeId, offsetMinutes = localOffsetMinutes() } = {}
) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return [];
  const buffer = Math.max(0, Number(callType?.bufferMinutes) || 0);
  const live = (Array.isArray(bookings) ? bookings : []).filter(
    (booking) => booking?.startsAt instanceof Date && isActiveBooking(booking)
  );
  const at = (minutes) => slotInstant(date, minutes);

  return (Array.isArray(dayShifts) ? dayShifts : [])
    .flatMap((shift) => slotsInEffect(shift, callType, offsetMinutes, date))
    .sort((a, b) => a.startMinutes - b.startMinutes)
    .map(({ startMinutes, endMinutes }) => {
      const startsAt = at(startMinutes);
      const endsAt = at(endMinutes);
      const booking =
        live.find((candidate) => {
          const bookingEnd = candidate.endsAt instanceof Date ? candidate.endsAt : candidate.startsAt;
          const bookingBuffer =
            bufferByCallTypeId?.get(String(candidate.shift_call_type_id)) || 0;
          return (
            startsAt.getTime() < bookingEnd.getTime() + bookingBuffer * 60000 &&
            candidate.startsAt.getTime() < endsAt.getTime() + buffer * 60000
          );
        }) || null;
      return { startsAt, endsAt, booking };
    });
}

/**
 * The seven local days of the Monday-first week containing `date`.
 * `isPast` is a whole local day before today; today itself is never past.
 *
 * @param {Date} [date]
 * @param {Date} [today] - Injectable for tests.
 * @returns {Array<{ date: Date, key: string, dayNumber: number, isToday: boolean, isPast: boolean }>}
 */
export function buildWeek(date = new Date(), today = new Date()) {
  const todayKey = formatLocalDate(today);
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + index);
    const key = formatLocalDate(day);
    // `YYYY-MM-DD` keys compare in date order as strings.
    return { date: day, key, dayNumber: index + 1, isToday: key === todayKey, isPast: key < todayKey };
  });
}

/**
 * The count a week-table row shows: how many **regular-call** slots the day's
 * shifts hold — the steady state, since every call after a client's first is
 * a regular one. Falls back to the first-call tier when only that is set, and
 * answers null when the department has no call types at all (there is no
 * number to show, rather than a zero). With the row's `date`, only slots that
 * start while their shift is in effect count, as in `shiftSlotsOnDate`.
 *
 * @param {Array<Object>} dayShifts - One day's local shifts.
 * @param {Record<number, Object>} tiers - From `callTypesByTier`.
 * @param {number} [offsetMinutes]
 * @param {Date} [date] - The local calendar day the row shows.
 * @returns {number|null}
 */
export function countDaySlots(dayShifts, tiers, offsetMinutes = localOffsetMinutes(), date) {
  const tier = callTierList(tiers).pop();
  if (!tier) return null;
  return (Array.isArray(dayShifts) ? dayShifts : []).reduce(
    (sum, shift) => sum + slotsInEffect(shift, tiers[tier.callNo], offsetMinutes, date).length,
    0
  );
}

/** The shift form's time step. The server takes any minute; this keeps the list short. */
export const SHIFT_STEP_MINUTES = 30;

const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** "Monday" for ISO day 1 … "Sunday" for 7. */
export function weekdayName(dayNumber) {
  return WEEKDAY_NAMES[dayNumber - 1] || "";
}

/** One time in the shift form's selects: "9:00 PM", or midnight spelled out as an end. */
export function formatTimeOption(minutes) {
  if (minutes >= MINUTES_PER_DAY) return "12:00 AM (midnight)";
  return formatTimeOfDay(minutes).toUpperCase();
}

/**
 * The times a shift form offers: starts from 00:00 to 23:30, ends from 00:30
 * to midnight, every `SHIFT_STEP_MINUTES`. `include` adds a value that is off
 * that grid — an existing shift written some other way must still be
 * selectable as itself, or opening it to edit would silently move it.
 *
 * @param {"start"|"end"} kind
 * @param {number} [include]
 * @returns {Array<{ value: number, label: string }>}
 */
export function shiftTimeOptions(kind, include) {
  const values = [];
  const first = kind === "end" ? SHIFT_STEP_MINUTES : 0;
  const last = kind === "end" ? MINUTES_PER_DAY : MINUTES_PER_DAY - SHIFT_STEP_MINUTES;
  for (let minute = first; minute <= last; minute += SHIFT_STEP_MINUTES) values.push(minute);
  if (Number.isFinite(include) && !values.includes(include)) {
    values.push(include);
    values.sort((a, b) => a - b);
  }
  return values.map((value) => ({ value, label: formatTimeOption(value) }));
}

/**
 * Why a shift draft cannot be saved, or null. Mirrors what the server would
 * refuse — no days, end not after start, overlapping another of the coach's
 * shifts on the same day (half open: touching is fine) — so the refusal is
 * explained before the request rather than after it.
 *
 * With `now`, an overlap names the date the new shift would first run on
 * (`nextShiftStart`) — "today", or e.g. "Tue 3 Nov" — instead of the weekday.
 *
 * @param {{ dayNumbers: Array<number>, startMinutes: number, endMinutes: number }} draft
 * @param {Array<Object>} localShifts - The coach's shifts, from `utcShiftsToLocal`.
 * @param {string} [ignoreKey] - The shift being edited.
 * @param {Date} [now] - When the shift is being added.
 * @returns {string|null}
 */
export function validateShiftDraft(draft, localShifts, ignoreKey, now) {
  const days = Array.isArray(draft?.dayNumbers) ? draft.dayNumbers : [];
  if (days.length === 0) return "Pick at least one day.";
  if (!(draft.endMinutes > draft.startMinutes)) return "The shift has to end after it starts.";
  for (const dayNumber of [...days].sort((a, b) => a - b)) {
    const clash = findShiftOverlap(
      { dayNumber, startMinutes: draft.startMinutes, endMinutes: draft.endMinutes },
      localShifts,
      ignoreKey
    );
    if (clash) {
      const range = formatMinutesRange(clash.startMinutes, clash.endMinutes);
      if (!now) return `Overlaps the ${range} shift on ${weekdayName(dayNumber)}.`;
      // The first date both are in effect: a clash that starts later than the
      // new shift's first run (added for a later week) first collides then.
      const firstRun = nextShiftStart(now, dayNumber, draft.startMinutes, draft.endMinutes);
      const clashFrom = clash.effectiveFrom ? localDayStart(clash.effectiveFrom) : null;
      const overlapOn =
        clashFrom && clashFrom > firstRun ? firstWeekdayFrom(clashFrom, dayNumber) : firstRun;
      if (localDayStart(overlapOn).getTime() === localDayStart(now).getTime()) {
        return `Overlaps today's ${range} shift.`;
      }
      const date = overlapOn.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      return `Overlaps the ${range} shift on ${date}.`;
    }
  }
  return null;
}

/** "28 Sep – 4 Oct 2026" for a `buildWeek` result. */
export function formatWeekRange(week) {
  const days = Array.isArray(week) ? week : [];
  if (days.length === 0) return "";
  const first = days[0].date;
  const last = days[days.length - 1].date;
  const dayMonth = (date) =>
    date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${dayMonth(first)} – ${dayMonth(last)} ${last.getFullYear()}`;
}

/** "7:00 – 10:00 AM" for a local shift or slot in minutes since midnight. */
export function formatMinutesRange(startMinutes, endMinutes) {
  const start = formatTimeOfDay(startMinutes);
  const end = endMinutes >= MINUTES_PER_DAY ? "12:00 am" : formatTimeOfDay(endMinutes);
  // Midnight at the end reads "12:00 AM", so it is in the morning half.
  const endIsAm = endMinutes >= MINUTES_PER_DAY || endMinutes < 720;
  const sameHalf = (startMinutes < 720) === endIsAm;
  return (sameHalf ? `${start.replace(/ (am|pm)$/, "")} – ${end}` : `${start} – ${end}`)
    .replace(/am/g, "AM")
    .replace(/pm/g, "PM");
}

/** "Wed 2 Sep 2026" — the date heading used under the calendar and in Step 2. */
export function formatDayHeading(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * "Fri, 11 Sept" — the same heading with the year dropped, for the dashboard's
 * day card and client-activity rows, which sit right next to a visible month
 * calendar and don't need it repeated.
 */
export function formatShortDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/* ------------------------------------------------------------------ *
 * Off-shift bookings
 *
 * A call booked outside the coach's shift hours, at a time typed in rather
 * than picked from the generated grid. The grid is what clients book, so the
 * window must stay clear of every shift in effect that day.
 * ------------------------------------------------------------------ */

/**
 * A local date and two minutes-of-day → the call's start and end as Dates.
 *
 * @param {Date} date - Any time on the call's local day.
 * @param {number} startMinutes
 * @param {number} endMinutes
 * @returns {{ startsAt: Date, endsAt: Date }}
 */
export function windowToInstants(date, startMinutes, endMinutes) {
  const day = localDayStart(date);
  const at = (minutes) =>
    new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes);
  return { startsAt: at(startMinutes), endsAt: at(endMinutes) };
}

/**
 * Why an off-shift window can't be booked, or null when it can.
 *
 * Checked in order: a missing field, an end not after the start, a start
 * already past (the server refuses one for anyone but an executive), then any
 * shift in effect that day it touches. Half open, like `findShiftOverlap`:
 * a call ending as a shift starts is fine.
 *
 * @param {Object} params
 * @param {Date|null} params.date
 * @param {number|null} params.startMinutes
 * @param {number|null} params.endMinutes
 * @param {Array<Object>} params.dayShifts - `shiftsForDate(date)`.
 * @param {Date} [params.now]
 * @returns {{ kind: "incomplete"|"order"|"past"|"shift", shift?: Object }|null}
 */
export function offShiftWindowError({ date, startMinutes, endMinutes, dayShifts, now = new Date() }) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return { kind: "incomplete" };
  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) return { kind: "incomplete" };
  if (endMinutes <= startMinutes) return { kind: "order" };
  if (windowToInstants(date, startMinutes, endMinutes).startsAt <= now) return { kind: "past" };
  const shift = (Array.isArray(dayShifts) ? dayShifts : []).find(
    (candidate) => startMinutes < candidate.endMinutes && candidate.startMinutes < endMinutes
  );
  return shift ? { kind: "shift", shift } : null;
}

/**
 * The `from_date`/`to_date` to read one local day's call type with from
 * `fetch-coach-slot`. Its dates resolve against UTC midnight and `to_date` is
 * exclusive, so the day is widened by one either side.
 *
 * @param {Date} date
 * @returns {{ fromDate: string, toDate: string }}
 */
export function slotDayWindow(date) {
  const day = localDayStart(date);
  const from = new Date(day.getFullYear(), day.getMonth(), day.getDate() - 1);
  const to = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 2);
  return { fromDate: formatLocalDate(from), toDate: formatLocalDate(to) };
}

/**
 * One local day as the inclusive ISO instants `fetch-booking-records` compares
 * `start_time` against — `monthRange`, a day wide.
 *
 * @param {Date} date
 * @returns {{ fromDate: string, toDate: string }}
 */
export function dayRange(date) {
  const day = localDayStart(date);
  const to = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999);
  return { fromDate: day.toISOString(), toDate: to.toISOString() };
}

/**
 * The client's confirmed calls among a day's booking rows, earliest first —
 * what stops an off-shift call being booked for them that day. Rescheduled
 * counts as confirmed: it is the status a replacement call starts in.
 *
 * @param {Array<Object>} records - From `fetch-booking-records`.
 * @param {string} userId
 * @returns {Array<Object>}
 */
export function confirmedCallsFor(records, userId) {
  const held = new Set([STATUS.CONFIRMED, STATUS.RESCHEDULED]);
  return (Array.isArray(records) ? records : [])
    .filter(
      (record) =>
        userId != null &&
        String(record?.user_id) === String(userId) &&
        held.has(normaliseStatus(record?.booking_status))
    )
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
}
